import { Button, Icon } from '../../../ui';

/**
 * THE DAMAGED-VIEW BANNER FOR A FILTER THAT WILL NOT RUN (D11 M4, D17 M8).
 *
 * Two situations reach it and both are the same shape of problem - a stored filter this
 * reader's page cannot execute - so they get one surface rather than two:
 *
 *  - the server REFUSED the filter because the reader may not read enough of an object it
 *    looks at. The list is not a list; the page renders this instead;
 *  - the stored filter did not PARSE on the way out of the database (a rollback below this
 *    module). The page refuses to run the view at all, because `filter: null` alone is
 *    indistinguishable from "no filter" and running it would show every record under a
 *    view name that promises fewer.
 *
 * IT DOES NOT RETRY UNFILTERED, and that is the decision rather than an omission. Widening
 * a list on a refusal is the failure this module exists to prevent, one step further along:
 * the reader sees the whole table and the view still says it is narrowed. **Clear filter**
 * is the only path forward and it is a CLICK - nothing widens without one.
 *
 * `role="alert"` and not `status`, unlike `ViewIssueBanner` beside it: that one is a notice
 * about a page which is working, and this one has replaced the page.
 */
export interface FilterRefusedBannerProps {
  readonly message: string;
  readonly onClear: () => void;
}

export function FilterRefusedBanner({ message, onClear }: FilterRefusedBannerProps) {
  return (
    /*
      A COLUMN UNTIL THERE IS ROOM FOR A ROW (screenshot round, wave 2). `flex-wrap` alone
      does not wrap a flexible child: the paragraph SHRANK to keep the button beside it, and
      on a phone this sentence - the longest copy in the module - came out eight lines tall
      in a 150px column beside a button with the whole right half to itself.
    */
    <div
      role="alert"
      className="flex flex-col gap-3 rounded-surface border border-warning bg-warning-subtle px-4 py-3 sm:flex-row sm:items-start"
    >
      <Icon name="warning" className="mt-0.5 hidden shrink-0 text-warning sm:block" size="1rem" />
      <p className="min-w-0 flex-1 text-sm text-ink">
        <Icon
          name="warning"
          className="mr-2 inline-block align-[-0.15em] text-warning sm:hidden"
          size="1rem"
        />
        {message}
      </p>
      <Button className="self-start" variant="secondary" size="sm" onClick={onClear}>
        Clear filter
      </Button>
    </div>
  );
}
